import { CustomLink } from "@/components/CustomLink";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG.client";
import type { MenuItemType, MenuType } from "@/types/baseTypes";
import type { OptionsNavigationItemType } from "@/types/OptionType";
import { customSanitize } from "@/utils/customSanitize";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { CulinaryCard } from "./CulinaryCard";
import { isLink } from "../utils";
import styles from "./MegaMenu.module.scss";
import BottomLink from "./BottomLink";

export type MegaMenuKeyType = "hotel" | "health" | "sport" | "kulinarik";

export type MegaMenuHighlightType = {
  id: number;
  title: string;
  subline: string;
  href: string;
};

export type MegaMenuHighlightsType = Partial<
  Record<MegaMenuKeyType, MegaMenuHighlightType[]>
>;

export type MegaMenuColumnType = {
  header: MenuItemType;
  links: MenuItemType[];
  bottomLink?: MenuItemType;
};

interface MegaMenuProps {
  item: MenuItemType;
  isOpen: boolean;
  option: OptionsNavigationItemType;
  highlights: MegaMenuHighlightType[];
  topMenu: MenuType | null;
  isCulinary?: boolean;
  onClose: () => void;
  onNavigate: () => void;
}

const hasClass = (item: MenuItemType, className: string) =>
  item.classes?.includes(className);

const stripHtmlTags = (value: string) => value.replace(/<[^>]*>/g, "");

export const hasVisibleMegaMenuTitle = (item: MenuItemType) =>
  stripHtmlTags(customSanitize(item.title))
    .replace(/&nbsp;|&#160;/gi, " ")
    .trim().length > 0;

export const getMegaMenuColumns = (
  items: MenuItemType[],
): MegaMenuColumnType[] => {
  const columns: MegaMenuColumnType[] = [];

  items.forEach((childItem) => {
    if (hasClass(childItem, "group-header")) {
      columns.push({
        header: childItem,
        links: [],
      });
      return;
    }

    const currentColumn = columns[columns.length - 1];

    if (!currentColumn) return;

    if (hasClass(childItem, "group-bottom")) {
      currentColumn.bottomLink = childItem;
      return;
    }

    currentColumn.links.push(childItem);
  });

  return columns;
};

export const SanitizedText = ({ text }: { text: string }) => (
  <span dangerouslySetInnerHTML={{ __html: customSanitize(text) }} />
);

export const MegaMenu = ({
  item,
  isOpen,
  option,
  highlights,
  topMenu,
  isCulinary = false,
  onClose,
  onNavigate,
}: MegaMenuProps) => {
  const { getIcon, getLabel } = useGlobalContext();
  const columns = getMegaMenuColumns(item.child_items || []);
  const visibleHighlights = highlights.filter((highlight) => highlight.href);
  const groupCountClass =
    columns.length <= 1
      ? styles["has-one-group"]
      : columns.length === 2
        ? styles["has-two-groups"]
        : columns.length === 3
          ? styles["has-three-groups"]
          : "";

  return (
    <div
      className={`${styles["mega-menu"]} mega-menu scrollbar-width ${isOpen ? `${styles["is-open"]} is-open` : ""}`}
      aria-hidden={!isOpen}
      inert={!isOpen}
    >
      <div className={`${styles["mega-menu-panel"]} mega-menu-panel`}>
        {isCulinary ? (
          <div className={styles["mega-menu-culinary-grid"]}>
            {(item.child_items || []).map((childItem) => (
              <CulinaryCard
                key={childItem.ID ?? childItem.url}
                item={childItem}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        ) : (
          <div className={`${styles["mega-menu-grid"]} ${groupCountClass}`}>
            <div className={styles["mega-menu-groups"]}>
              {columns.map((column, columnIndex) => (
                <section
                  className={styles["mega-menu-column"]}
                  key={
                    column.header.ID ?? `${column.header.title}-${columnIndex}`
                  }
                  aria-labelledby={`mega-menu-column-${item.ID}-${columnIndex}`}
                >
                  <span
                    className={styles["mega-menu-title"]}
                    id={`mega-menu-column-${item.ID}-${columnIndex}`}
                  >
                    <SanitizedText text={column.header.title} />
                  </span>
                  <ul className={styles["mega-menu-list"]}>
                    {column.links.map((link, linkIndex) => (
                      <li key={link.ID ?? link.url}>
                        <CustomLink
                          className={styles["mega-menu-link"]}
                          href={link.url}
                          target={link.target}
                          onClick={onNavigate}
                          disablePrefetch
                          {...(columnIndex === 0 &&
                            linkIndex === 0 && {
                              "data-mega-menu-first-link": true,
                            })}
                        >
                          <SanitizedText text={link.title} />
                        </CustomLink>
                      </li>
                    ))}
                  </ul>
                  {column.bottomLink && (
                    <BottomLink link={column.bottomLink} getIcon={getIcon} />
                  )}
                </section>
              ))}
            </div>

            <section
              className={styles["mega-menu-highlights"]}
              aria-labelledby={`mega-menu-highlights-${item.ID}`}
            >
              <span
                className={styles["mega-menu-title"]}
                id={`mega-menu-highlights-${item.ID}`}
              >
                {option.submenu_title || getLabel("highlights", "Highlights")}
              </span>
              <ul className={styles["mega-menu-highlight-list"]}>
                {visibleHighlights.map((highlight) => (
                  <li key={highlight.id}>
                    <CustomLink
                      className={styles["mega-menu-highlight-link"]}
                      href={highlight.href}
                      target="_self"
                      onClick={onNavigate}
                      disablePrefetch
                    >
                      <span
                        className={styles["mega-menu-highlight-title"]}
                        dangerouslySetInnerHTML={{
                          __html: customSanitize(highlight.title),
                        }}
                      />
                      {highlight.subline && (
                        <span
                          className={styles["mega-menu-highlight-subline"]}
                          dangerouslySetInnerHTML={{
                            __html: customSanitize(highlight.subline),
                          }}
                        />
                      )}
                    </CustomLink>
                  </li>
                ))}
              </ul>
              {isLink(option.link_column_end) && (
                <BottomLink link={option.link_column_end} getIcon={getIcon} />
              )}
            </section>
          </div>
        )}

        <div className={styles["mega-menu-footer"]}>
          <div className={styles["mega-menu-footer-content"]}>
            {isLink(option.link_menu_footer) && (
              <CustomLink
                className={styles["mega-menu-footer-link"]}
                href={option.link_menu_footer.url}
                target={option.link_menu_footer.target}
                onClick={onNavigate}
                disablePrefetch
              >
                <span className={styles["mega-menu-footer-icon"]}>
                  <CustomSVG
                    width={32}
                    height={32}
                    src={getIcon("yellow_circle_arrow")}
                    wrapper={"span"}
                    aria-hidden="true"
                    className="is-flex"
                  />
                </span>
                <SanitizedText text={option.link_menu_footer.title} />
              </CustomLink>
            )}
            {option.content && (
              <div
                className={styles["mega-menu-footer-text"]}
                dangerouslySetInnerHTML={{
                  __html: customSanitize(option.content),
                }}
              />
            )}
          </div>

          {topMenu && topMenu.items.length > 0 && (
            <ul
              className={styles["mega-menu-utility-menu"]}
              aria-label={topMenu.name}
            >
              {topMenu.items.map((topMenuItem) => (
                <li key={topMenuItem.ID ?? topMenuItem.url}>
                  <CustomLink
                    href={topMenuItem.url}
                    target={topMenuItem.target}
                    onClick={onNavigate}
                    disablePrefetch
                  >
                    <SanitizedText text={topMenuItem.title} />
                  </CustomLink>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div
        className={styles["mega-menu-close-area"]}
        role="button"
        tabIndex={0}
        aria-label={getLabel("close", "Schließen")}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClose();
          }
        }}
      >
        <span className={`${styles["mega-menu-close-button"]}`}>
          <CustomSVG
            width={16}
            height={16}
            src={getIcon("close")}
            wrapper={"span"}
            aria-hidden="true"
          />
          <span>{getLabel("close", "Schließen")}</span>
        </span>
      </div>
    </div>
  );
};
